import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {FetchFileRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/FetchFileRequest';
import {FetchFilesRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/FetchFilesRequest';
import {FileResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/FileResponse';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {ABinaryData} from '@mintdata/mint-kit/api/values/ABinaryData';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {ADateTime} from '@mintdata/mint-kit/api/values/ADateTime';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import {JsonUtil} from '@mintdata/mint-kit/impl/util/JsonUtil';
import bind from 'bind-decorator';

export class FileFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'file';
    }

    @Function({
        title: 'FILE(id, [with-meta-info=false])',
        tip: 'Fetches information about previously uploaded file.‌',
        alias: 'FILE',
        example: 'FILE("http://example.com/image.png", true)',
        icon: '',
        returns:
            {
                title: 'value',
                tip: 'Returns a frame with information about file.',
                type: SheetType.AnyIValue
            }
    })
    @bind
    public getFile(
        @Param({
            title: 'id',
            tip: 'File id',
            type: Type.String
        }) fileId:AString,
        @Param({
            title: 'withMeta',
            tip: 'With meta?',
            type: Type.Boolean,
            optional: true
        }) withMeta:ABoolean):Promise<IFrame | AString | AError>
    {
        const organizationSid = commonControllers.context.currentOrganizationSid();

        return API.files
            .getFile(organizationSid, fileId.value)
            .then((response) =>
                {
                    if( response instanceof ApiError )
                        return new AError(response.message || response.error);

                    const needMeta = withMeta && withMeta.value;
                    return needMeta
                        ? this._convertFileResponsesToFrame([response])
                        : new AString(response.accessUrl);
                }
            );
    }

    @Function({
        title: 'FILES(urls)',
        tip: 'Fetches information about multiple previously uploaded files.‌',
        alias: 'FILES',
        example: 'FILES({"http://example.com/image1.png", "http://example.com/image2.png"})',
        icon: '',
        returns:
            {
                title: 'value',
                tip: 'Returns a frame with information about files.',
                type: Type.Frame
            }
    })
    @bind
    public getFiles(
        @Param({
            title: 'urls',
            tip: 'Urls to fetch',
            type: Type.Frame
        }) urlsFrame:IFrame):Promise<IFrame | AError>
    {
        const organizationSid = commonControllers.context.currentOrganizationSid();

        const idsOrError = this._convertAllFrameCellsToStrings(urlsFrame);
        if( Type.isInstanceOf(idsOrError, Type.Error) )
            return Promise.resolve(idsOrError as AError);

        const ids = (idsOrError as Array<string>).join(',');

        return API.files
            .getFiles(organizationSid, ids)
            .then((response) =>
                response instanceof ApiError
                    ? new AError(response.message || response.error)
                    : this._convertFileResponsesToFrame(response.files)
            );
    }

    @Function({
        title: 'SAVE_FILE(url)',
        tip: 'Fetches file from a provided url and saves into cloud.‌',
        alias: 'SAVE_FILE',
        example: 'SAVE_FILE("http://example.com/image.png")',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns a frame with information about uploaded file.',
                type: Type.Frame
            }
    })
    @bind
    public saveFile(
        @Param({
            title: 'url',
            tip: 'Url to fetch',
            type: Type.String
        }) url:AString):Promise<IFrame | AError>
    {
        const organizationSid = commonControllers.context.currentOrganizationSid();
        const request = new FetchFileRequest(url.value, null);

        return API.files
            .fetchAndSave(organizationSid, request)
            .then((response) =>
                response instanceof ApiError
                    ? new AError(response.message || response.error)
                    : this._convertFileResponsesToFrame([response])
            );
    }

    @Function({
        title: 'SAVE_FILES(urls)',
        tip: 'Fetches multiple files from a provided urls and saves them into cloud.‌',
        alias: 'SAVE_FILES',
        example: 'SAVE_FILES({"http://example.com/image1.png", "http://example.com/image2.png"})',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns a frame with information about uploaded files.',
                type: Type.Frame
            }
    })
    @bind
    public saveFiles(
        @Param({
            title: 'urls',
            tip: 'Urls to fetch',
            type: Type.Frame
        }) urlsFrame:IFrame):Promise<IFrame | AError>
    {
        const organizationSid = commonControllers.context.currentOrganizationSid();

        const urlsOrError = this._convertAllFrameCellsToStrings(urlsFrame);
        if( Type.isInstanceOf(urlsOrError, Type.Error) )
            return Promise.resolve(urlsOrError as AError);

        const urls = urlsOrError as Array<string>;

        const files = urls.map(url => new FetchFileRequest(url, null));
        const request = new FetchFilesRequest(files);

        return API.files
            .fetchAndSaveMultiple(organizationSid, request)
            .then((response) =>
                response instanceof ApiError
                    ? new AError(response.message || response.error)
                    : this._convertFileResponsesToFrame(response.files)
            );
    }

    @Function({
        title: 'UPLOAD_FILE(fileData, [mimeType, fileExtension, returnDetails])',
        tip: 'Upload binary data to cloud and returns info about uploaded file',
        alias: 'UPLOAD_FILE',
        example: 'UPLOAD_FILE(A1)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns a frame with information about uploaded file.',
                type: Type.Frame
            }
    })
    @bind
    public async uploadFile(
        @Param({
            title: 'fileData',
            tip: 'File data to upload',
            type: Type.BinaryData
        }) fileData:ABinaryData,
        @Param({
            title: 'mimeType',
            tip: 'Mime type to use for a file',
            type: Type.String,
            optional: true
        }) mimeType:AString,
        @Param({
            title: 'fileExtension',
            tip: 'File extension to use for a file',
            type: Type.String,
            optional: true
        }) fileExtension:AString,
        @Param({
            title: 'returnDetails',
            tip: 'Return full details for an uploaded file, or just an url',
            type: Type.Boolean,
            optional: true
        }) returnDetails:ABoolean):Promise<IFrame | AString | AError>
    {
        if( !Type.isInstanceOf(fileData, Type.BinaryData) )
            return new AError('#INVALID_FILE_DATA_VALUE');

        const organizationSid = commonControllers.context.currentOrganizationSid();

        const fileSuffix = fileExtension && fileExtension.value && fileExtension.value.length > 0 ? '.' + fileExtension.value : '';
        const fileName = `uploaded-file${fileSuffix}`;

        const formData = new FormData();
        formData.append('filename', fileName);

        if( mimeType && mimeType.value && mimeType.value.length > 0 )
            formData.append('forceContentType', mimeType.value);

        if( fileExtension && fileExtension.value && fileExtension.value.length > 0 )
            formData.append('forceFileExtension', fileExtension.value);

        // !! contents should go last, so it can be parsed in a stream-fashion on a server
        formData.append('contents', new Blob([fileData.value]));

        const response = await API.files.uploadFile(organizationSid, formData);
        if( response instanceof ApiError )
            return new AError(response.message || response.error);

        if( returnDetails && returnDetails.value )
            return this._convertFileResponsesToFrame([response]);

        return new AString(response.accessUrl);
    }

    private _convertFileResponsesToFrame(responses:Array<FileResponse>):IFrame
    {
        // populate fields
        const fields = [
            new FrameField('file_id', Type.String),
            new FrameField('access_url', Type.String),
            new FrameField('width', Type.Number),
            new FrameField('height', Type.Number),
            new FrameField('created_at', Type.DateTime),
            new FrameField('type', Type.String),
            new FrameField('sub_type', Type.String),
            new FrameField('file_size', Type.Integer)
        ];

        // populate data
        const data = responses.map(file =>
        {
            const contentType = file.contentType || '';
            const slashInd = contentType.indexOf('/');
            const type = slashInd >= 0 ? contentType.substring(0, slashInd) : contentType;
            const subType = slashInd >= 0 ? contentType.substring(slashInd + 1) : '';

            const metadata = file.metadata || {};

            return [
                JsonUtil.convertJsonValueToIValue(file.id),
                JsonUtil.convertJsonValueToIValue(file.accessUrl),
                JsonUtil.convertJsonValueToIValue(metadata['width']),
                JsonUtil.convertJsonValueToIValue(metadata['height']),
                ADateTime.fromJSDate(new Date(file.createdAt)),
                JsonUtil.convertJsonValueToIValue(type),
                JsonUtil.convertJsonValueToIValue(subType),
                JsonUtil.convertJsonValueToIValue(file.contentLength)
            ];
        });

        return Frames.fromFieldsAndData(fields, data);
    }

    private _convertAllFrameCellsToStrings(frame:IFrame):Array<string> | AError
    {
        const values = frame.getRecords()
            .map(record => record.getCells())
            .reduce((values, rowValues) => values.concat(rowValues), []);

        const result = [];
        for( const value of values )
        {
            const coerced = CoercionUtils.coerce(value, Type.String, null);
            if( Type.isInstanceOf(coerced, Type.Error) )
                return new AError('#CANT_COERCE_URL_TO_STRING');

            const sValue = (coerced as AString).value;
            if( sValue && sValue.trim().length > 0 )
                result.push(sValue);
        }

        return result;
    }
}
